/**
 * CR413: Create Merchant Dialog
 * Modal for creating new merchants with smart defaults.
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertCircle } from "lucide-react";
import { useMerchantCategoriesList } from "@/hooks/useMerchantCategories";
import { normalizeMerchantId } from "@/lib/utils/merchantIdNormalization";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/** localStorage keys for remembering last-selected values */
const LS_LAST_TYPE = "createMerchant.lastType";
const LS_LAST_CATEGORY = "createMerchant.lastCategoryId";

const MERCHANT_TYPES = [
  { value: "retailer", label: "Retailer" },
  { value: "issuer", label: "Issuer" },
  { value: "both", label: "Both" },
];

interface CreateMerchantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill values (e.g. from issuer candidate approval) */
  prefill?: {
    displayName?: string;
    merchantType?: string;
    hideUntilLogo?: boolean;
  };
}

export function CreateMerchantDialog({
  open,
  onOpenChange,
  prefill,
}: CreateMerchantDialogProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: categories = [] } = useMerchantCategoriesList();

  // Form state
  const [displayName, setDisplayName] = useState("");
  const [merchantKey, setMerchantKey] = useState("");
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false);
  const [merchantType, setMerchantType] = useState(
    () => localStorage.getItem(LS_LAST_TYPE) || "retailer"
  );
  const [categoryId, setCategoryId] = useState(
    () => localStorage.getItem(LS_LAST_CATEGORY) || ""
  );
  const [scanEnabled, setScanEnabled] = useState(true);
  const [hideUntilLogo, setHideUntilLogo] = useState(false);

  // Uniqueness check
  const [keyExists, setKeyExists] = useState(false);
  const [checkingKey, setCheckingKey] = useState(false);

  // Auto-generate key from display name
  useEffect(() => {
    if (!keyManuallyEdited && displayName) {
      setMerchantKey(normalizeMerchantId(displayName));
    }
  }, [displayName, keyManuallyEdited]);

  // Smart defaults based on merchant type
  useEffect(() => {
    if (merchantType === "issuer") {
      setScanEnabled(false);
      setHideUntilLogo(true);
    } else {
      setScanEnabled(true);
      setHideUntilLogo(false);
    }
  }, [merchantType]);

  // Apply prefill when dialog opens
  useEffect(() => {
    if (open && prefill) {
      if (prefill.displayName) {
        setDisplayName(prefill.displayName);
        setMerchantKey(normalizeMerchantId(prefill.displayName));
      }
      if (prefill.merchantType) {
        setMerchantType(prefill.merchantType);
      }
      if (prefill.hideUntilLogo !== undefined) {
        setHideUntilLogo(prefill.hideUntilLogo);
      }
    }
  }, [open, prefill]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setDisplayName("");
      setMerchantKey("");
      setKeyManuallyEdited(false);
      setKeyExists(false);
    }
  }, [open]);

  // Debounced uniqueness check
  useEffect(() => {
    if (!merchantKey || merchantKey.length < 2) {
      setKeyExists(false);
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingKey(true);
      try {
        const { data } = await supabase
          .from("merchants")
          .select("merchant_key")
          .eq("merchant_key", merchantKey)
          .maybeSingle();
        setKeyExists(!!data);
      } catch {
        // ignore
      } finally {
        setCheckingKey(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [merchantKey]);

  // Computed
  const isHidden = hideUntilLogo;
  const normalizedKey = useMemo(() => normalizeMerchantId(merchantKey), [merchantKey]);
  const canSubmit =
    displayName.trim().length > 0 &&
    normalizedKey.length > 0 &&
    categoryId.length > 0 &&
    !keyExists &&
    !checkingKey;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("rpc_create_merchant", {
        p_merchant_key: normalizedKey,
        p_display_name: displayName.trim(),
        p_merchant_type: merchantType,
        p_category_id: categoryId,
        p_scan_enabled: scanEnabled,
        p_is_hidden: isHidden,
        p_directory_status: "pending",
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (createdKey) => {
      // Remember preferences
      localStorage.setItem(LS_LAST_TYPE, merchantType);
      localStorage.setItem(LS_LAST_CATEGORY, categoryId);

      // Invalidate directory queries
      queryClient.invalidateQueries({ queryKey: ["merchants-directory"] });
      queryClient.invalidateQueries({ queryKey: ["merchants-pending-count"] });

      toast({
        title: "Merchant created",
        description: `"${displayName.trim()}" created as ${merchantType}. ${
          !isHidden ? "" : "Hidden until logo is added."
        }`,
      });

      onOpenChange(false);
      navigate(`/admin/merchants/${createdKey}`);
    },
    onError: (err: Error) => {
      toast({
        title: "Error creating merchant",
        description: err.message || "Failed to create merchant",
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Add Merchant</DialogTitle>
          <DialogDescription>
            Create a new merchant in the directory. It will start in Pending
            status for review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Display Name */}
          <div className="space-y-1.5">
            <Label htmlFor="displayName">
              Display Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Walmart, Athleta, Fortnite"
              autoFocus
            />
          </div>

          {/* Merchant Key */}
          <div className="space-y-1.5">
            <Label htmlFor="merchantKey">
              Merchant Key <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Input
                id="merchantKey"
                value={merchantKey}
                onChange={(e) => {
                  setMerchantKey(e.target.value);
                  setKeyManuallyEdited(true);
                }}
                placeholder="auto-generated-slug"
                className={keyExists ? "border-destructive pr-8" : "pr-8"}
              />
              {checkingKey && (
                <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            {normalizedKey && normalizedKey !== merchantKey && (
              <p className="text-xs text-muted-foreground">
                Will be saved as: <code className="font-mono">{normalizedKey}</code>
              </p>
            )}
            {keyExists && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                This merchant key already exists
              </p>
            )}
          </div>

          {/* Merchant Type */}
          <div className="space-y-1.5">
            <Label>
              Merchant Type <span className="text-destructive">*</span>
            </Label>
            <Select value={merchantType} onValueChange={setMerchantType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MERCHANT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category */}
          <div className="space-y-1.5">
            <Label>
              Category <span className="text-destructive">*</span>
            </Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Scan Enabled */}
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <Label htmlFor="scanEnabled" className="text-sm font-medium">
                Scan Enabled
              </Label>
              <p className="text-xs text-muted-foreground">
                {merchantType === "issuer"
                  ? "Issuers typically don't need scanning"
                  : "Enable flyer/web scanning for this merchant"}
              </p>
            </div>
            <Switch
              id="scanEnabled"
              checked={scanEnabled}
              onCheckedChange={setScanEnabled}
            />
          </div>

          {/* Hide Until Logo (issuer-focused) */}
          <div className="flex items-start gap-2 rounded-md border px-3 py-2">
            <Checkbox
              id="hideUntilLogo"
              checked={hideUntilLogo}
              onCheckedChange={(v) => setHideUntilLogo(!!v)}
              className="mt-0.5"
            />
            <div>
              <Label htmlFor="hideUntilLogo" className="text-sm font-medium">
                Hide until logo is added
              </Label>
              <p className="text-xs text-muted-foreground">
                Merchant will be hidden in public views until a logo is uploaded
              </p>
            </div>
          </div>

          {/* Summary badges */}
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline">Pending</Badge>
            <Badge variant={scanEnabled ? "default" : "secondary"}>
              {scanEnabled ? "Scan On" : "Scan Off"}
            </Badge>
            {isHidden && <Badge variant="secondary">Hidden</Badge>}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!canSubmit || createMutation.isPending}
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Merchant"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
